import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
 ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { Fir5471ScheduleG1Service } from './fir-5471-schedule-g1.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

interface ScheduleG1LineValue {
  VALUE?: string | null;  // Can be undefined when field doesn't exist in backend response
  line_attrib_key: string;
  disabled?: boolean;
}

interface ScheduleG1CheckboxValue {
  VALUE: string | null;
  line_attrib_key: string;
  attrib_name: string;
  property_type: string;
}

interface ScheduleG1LineItem {
  line_no: string;
  line_description: string;
  propertyType: string;
  isEditableFlag: string;
  attrib_name?: string | string[];
  value?: ScheduleG1LineValue | ScheduleG1LineValue[];
  value_comparable?: ScheduleG1CheckboxValue;
  value_income?: ScheduleG1CheckboxValue;
  value_acquisition?: ScheduleG1CheckboxValue;
  value_market?: ScheduleG1CheckboxValue;
  value_residual?: ScheduleG1CheckboxValue;
  value_unspecified?: ScheduleG1CheckboxValue;
  saveParams: {
    tax_year: string;
    trans_type_key: string;
    trans_Details_key: string;
    occurrence: string;
    form_key: string;
  };
}

@Component({
    selector: 'gtw-fir-5471-schedule-g1',
    templateUrl: './fir-5471-schedule-g1.component.html',
    styleUrls: ['./fir-5471-schedule-g1.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Fir5471ScheduleG1Component implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private unsubscribe$ = new Subject<void>();
  /** Dismisses stale JSON responses when row changes or a newer load starts. */
  private loadSeq = 0;
  private lastCombinationKey: string | number | undefined;
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;

  // Component state
  modalTitle = "Schedule G1 - Cost Sharing Arrangement";
  dataLoading = false;
  crudLoading = false;
  userMessage = '';
  minimise = false;
  private isInitialized = false;
  
  // Data properties
  irsF5471ScheduleG1Data: ScheduleG1LineItem[] = [];
  invalidSaveMessage = '';
  isSaveValidFlag = true;
  saveFlag = false;
  tempValue = '0';
  
  // Global configuration
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  globalService: any = {};
  filterParams: any = {};
  rowData: any = {};

  constructor(
    private fir5471ScheduleG1Service: Fir5471ScheduleG1Service,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? '',
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    const key = this.rowData?.HO_COMBINATION_KEY ?? this.rowData?.COMBINATION_KEY;
    if (this.lastCombinationKey !== undefined && key !== this.lastCombinationKey) {
      this.loadSeq++;
      this.irsF5471ScheduleG1Data = [];
    }
    this.lastCombinationKey = key;
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit() {
    if (this.isInitialized) return;
    
    // Check for base_url in different possible locations (matching other components)
    const baseUrl = this.baseURLs?.api || this.GENERAL_CONFIG?.base_url || this.GENERAL_CONFIG?.api || '';
    
    if (this.rowData && Object.keys(this.rowData).length > 0 && baseUrl) {
      // Normalize base_url
      if (!this.baseURLs?.api && baseUrl) {
        this.baseURLs = { ...this.baseURLs, api: baseUrl };
      }
      
      // Normalize rowData - ensure HO_COMBINATION_KEY exists
      if (!this.rowData.HO_COMBINATION_KEY && this.rowData.COMBINATION_KEY) {
        this.rowData.HO_COMBINATION_KEY = this.rowData.COMBINATION_KEY;
      }
      
      this.isInitialized = true;
      this.initLoadData();
    }
  }

  initLoadData() {
    this.loadIRSF5471ScheduleG1Data();
  }

  loadF5471ScheduleGLine7Data(parentSeq: number) {
    const params = {
      "action_code": '74ng9y',
      "tax_year": this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
      "scenario": this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
      "jcd_key": this.globalService?.globalParams?.jcd_key === '' || this.globalService?.globalParams?.jcd_key === undefined ? 250 : this.globalService?.globalParams?.jcd_key,
      "combination_key": this.rowData?.HO_COMBINATION_KEY
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.baseURLs.api + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe((data: any) => {
      if (parentSeq !== this.loadSeq) {
        return;
      }
      if (data.callSuccess === "1") {
        this.tempValue = data.jsonObject[0] === undefined ? '0' : data.jsonObject[0].ATTRIB_VALUE;
        if (this.tempValue == '0') {
          this.saveFlag = false;
        } else {
          this.saveFlag = true;
        }
      } else {
        if (parentSeq !== this.loadSeq) {
          return;
        }
        if (data.errorMessage) {
          this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
          this.userMessage = "!! Unable to perform Selected Action...";
        } else {
          this.gtwSnackbarService.openSnackBar("An error has occurred, if you continue to experience this issue please visit the help page to contact support.", 'gtw-snackbar--red');
          this.userMessage = "!! Unable to perform Selected Action...";
        }
      }
    });
  }

  loadIRSF5471ScheduleG1Data() {
    const seq = ++this.loadSeq;
    const params = {
      "action_code": '1k04gp',
      "tax_year": this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
      "scenario": this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
      "jcd_key": this.globalService?.globalParams?.jcd_key === '' || this.globalService?.globalParams?.jcd_key === undefined ? 250 : this.globalService?.globalParams?.jcd_key,
      "irs_form_no": 'F5471',
      "combination_key": this.rowData?.HO_COMBINATION_KEY
    };
    
    this.dataLoading = true;
    
    this.jsonObjectFactory.getJSONObj(
      this.baseURLs.api + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe((data: any) => {
      if (seq !== this.loadSeq) {
        return;
      }
      if (data.callSuccess === "1") {
        this.dataLoading = false;
        const irsF5471ScheduleG1Data = data.jsonObject;
        
        this.processScheduleG1Data(irsF5471ScheduleG1Data);
        this.loadF5471ScheduleGLine7Data(seq);
      } else {
        if (seq !== this.loadSeq) {
          return;
        }
        this.dataLoading = false;
        if (data.errorMessage) {
          this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
          this.userMessage = "!! Unable to perform Selected Action...";
        } else {
          this.gtwSnackbarService.openSnackBar("An error has occurred, if you continue to experience this issue please visit the help page to contact support.", 'gtw-snackbar--red');
          this.userMessage = "!! Unable to perform Selected Action...";
        }
      }
    }, (error: any) => {
      if (seq !== this.loadSeq) {
        return;
      }
      this.dataLoading = false;
      console.error('Schedule G1 HTTP Error:', error);
      this.gtwSnackbarService.openSnackBar('Error loading data: ' + error.message, 'gtw-snackbar--red');
    });
  }

  processScheduleG1Data(irsF5471ScheduleG1Data: any[]) {
    let i = 1;
    let previous_line_no = irsF5471ScheduleG1Data[i].line_no;
    
    while (i < irsF5471ScheduleG1Data.length) {
      const tempObj: any = {};
      let j = i;
      let count = 0;
      
      while (j < irsF5471ScheduleG1Data.length && previous_line_no === irsF5471ScheduleG1Data[j].line_no) {
        if (irsF5471ScheduleG1Data[j].attrib_value.split("~")[0] === "LINE_NO") {
          tempObj.propertyType = irsF5471ScheduleG1Data[j].PROPERTY_TYPE;
          tempObj.line_no = irsF5471ScheduleG1Data[j].line_no;
          tempObj.saveParams = {
            "tax_year": irsF5471ScheduleG1Data[j].object_id.split("~")[0],
            "trans_type_key": irsF5471ScheduleG1Data[j].object_id.split("~")[1],
            "trans_Details_key": irsF5471ScheduleG1Data[j].object_id.split("~")[3],
            "occurrence": irsF5471ScheduleG1Data[j].object_id.split("~")[4],
            "form_key": irsF5471ScheduleG1Data[j].object_id.split("~")[6]
          };
        } else if (irsF5471ScheduleG1Data[j].attrib_value.split("~")[0] === "LINE_DESCRIPTION") {
          tempObj.line_description = irsF5471ScheduleG1Data[j].attrib_value.split("~")[3];
        } else if (irsF5471ScheduleG1Data[j].attrib_value.split("~")[0] === "COMPARABLE_UNCONTROLLED_Y") {
          tempObj.value_comparable = {
            "VALUE": irsF5471ScheduleG1Data[j].VALUE,
            "line_attrib_key": irsF5471ScheduleG1Data[j].attrib_value.split("~")[1],
            "attrib_name": irsF5471ScheduleG1Data[j].attrib_value.split("~")[0],
            "property_type": irsF5471ScheduleG1Data[j].PROPERTY_TYPE
          };
        } else if (irsF5471ScheduleG1Data[j].attrib_value.split("~")[0] === "INCOME_METHOD_Y") {
          tempObj.value_income = {
            "VALUE": irsF5471ScheduleG1Data[j].VALUE,
            "line_attrib_key": irsF5471ScheduleG1Data[j].attrib_value.split("~")[1],
            "attrib_name": irsF5471ScheduleG1Data[j].attrib_value.split("~")[0],
            "property_type": irsF5471ScheduleG1Data[j].PROPERTY_TYPE
          };
        } else if (irsF5471ScheduleG1Data[j].attrib_value.split("~")[0] === "ACQUISITION_PRICE_METHOD_Y") {
          tempObj.value_acquisition = {
            "VALUE": irsF5471ScheduleG1Data[j].VALUE,
            "line_attrib_key": irsF5471ScheduleG1Data[j].attrib_value.split("~")[1],
            "attrib_name": irsF5471ScheduleG1Data[j].attrib_value.split("~")[0],
            "property_type": irsF5471ScheduleG1Data[j].PROPERTY_TYPE
          };
        } else if (irsF5471ScheduleG1Data[j].attrib_value.split("~")[0] === "MARKET_CAPITALIZATION_Y") {
          tempObj.value_market = {
            "VALUE": irsF5471ScheduleG1Data[j].VALUE,
            "line_attrib_key": irsF5471ScheduleG1Data[j].attrib_value.split("~")[1],
            "attrib_name": irsF5471ScheduleG1Data[j].attrib_value.split("~")[0],
            "property_type": irsF5471ScheduleG1Data[j].PROPERTY_TYPE
          };
        } else if (irsF5471ScheduleG1Data[j].attrib_value.split("~")[0] === "RESIDUAL_PROFIT_Y") {
          tempObj.value_residual = {
            "VALUE": irsF5471ScheduleG1Data[j].VALUE,
            "line_attrib_key": irsF5471ScheduleG1Data[j].attrib_value.split("~")[1],
            "attrib_name": irsF5471ScheduleG1Data[j].attrib_value.split("~")[0],
            "property_type": irsF5471ScheduleG1Data[j].PROPERTY_TYPE
          };
        } else if (irsF5471ScheduleG1Data[j].attrib_value.split("~")[0] === "UNSPECIFIED_METHODS_Y") {
          tempObj.value_unspecified = {
            "VALUE": irsF5471ScheduleG1Data[j].VALUE,
            "line_attrib_key": irsF5471ScheduleG1Data[j].attrib_value.split("~")[1],
            "attrib_name": irsF5471ScheduleG1Data[j].attrib_value.split("~")[0],
            "property_type": irsF5471ScheduleG1Data[j].PROPERTY_TYPE
          };
        } else {
          if (count > 0) {
            if (count == 1) {
              tempObj.attrib_name = [tempObj.attrib_name];
              tempObj.value = [tempObj.value];
            }
            tempObj.attrib_name[count] = irsF5471ScheduleG1Data[j].attrib_value.split("~")[0];
            tempObj.value[count] = {
              "VALUE": irsF5471ScheduleG1Data[j].VALUE,
              "line_attrib_key": irsF5471ScheduleG1Data[j].attrib_value.split("~")[1]
            };
          } else {
            tempObj.propertyType = irsF5471ScheduleG1Data[j].PROPERTY_TYPE;
            tempObj.isEditableFlag = 'Y';
            tempObj.attrib_name = irsF5471ScheduleG1Data[j].attrib_value.split("~")[0];
            tempObj.value = {
              "VALUE": tempObj.line_no == 'FormNo' ? 'Form 1' : irsF5471ScheduleG1Data[j].VALUE,
              "line_attrib_key": irsF5471ScheduleG1Data[j].attrib_value.split("~")[1]
            };
          }
        }
        previous_line_no = irsF5471ScheduleG1Data[j].line_no;
        if (irsF5471ScheduleG1Data[j].PROPERTY_TYPE == '13') {
          count++;
        }
        j++;
      }
      
      if (j < irsF5471ScheduleG1Data.length) {
        previous_line_no = irsF5471ScheduleG1Data[j].line_no;
      }
      
      const tempIndex = this.irsF5471ScheduleG1Data.length;
      this.irsF5471ScheduleG1Data.push(tempObj);
      i = j;
    }
    
  }

  onRadioBtnSelection(row: ScheduleG1LineItem, index: number, value: string) {
    if (Array.isArray(row.value)) {
      if (value === 'Y') {
        // Use undefined instead of null - matches AngularJS JSON.stringify behavior
        // When undefined, JSON.stringify omits the field entirely
        row.value[1].VALUE = undefined;
      } else if (value === 'N') {
        row.value[0].VALUE = undefined;
      }
    }
  }

  checkingForValidation(row: ScheduleG1LineItem, index: number) {
    if (row.line_no === '5a') {
      if (Array.isArray(row.value) && row.value[1].VALUE === 'N') {
        this.findIndex_1(this.irsF5471ScheduleG1Data, 'LINE_5B_VALUE_5471_G1', 'N');
        this.findIndex_1(this.irsF5471ScheduleG1Data, 'UNSPECIFIED_METHODS_N', 'N');
      } else {
        this.findIndex_1(this.irsF5471ScheduleG1Data, 'LINE_5B_VALUE_5471_G1', 'Y');
        this.findIndex_1(this.irsF5471ScheduleG1Data, 'UNSPECIFIED_METHODS_N', 'Y');
      }
    }
    
    if (row.line_no === '5c') {
      if (!row.value_acquisition || row.value_acquisition.VALUE == '0') {
        row.value_acquisition!.VALUE = null;
      }
      if (!row.value_comparable || row.value_comparable.VALUE == '0') {
        row.value_comparable!.VALUE = null;
      }
      if (!row.value_income || row.value_income.VALUE == '0') {
        row.value_income!.VALUE = null;
      }
      if (!row.value_market || row.value_market.VALUE == '0') {
        row.value_market!.VALUE = null;
      }
      if (!row.value_residual || row.value_residual.VALUE == '0') {
        row.value_residual!.VALUE = null;
      }
      if (!row.value_unspecified || row.value_unspecified.VALUE == '0') {
        row.value_unspecified!.VALUE = null;
      }
    }
  }

  findIndex_1(data: ScheduleG1LineItem[], attribName: string, value: string) {
    const findData = data.find(x => x.attrib_name === attribName);
    if (!findData) return;
    
    if (findData.propertyType == '7') {
      findData.isEditableFlag = value;
      if (!Array.isArray(findData.value) && findData.value) {
        findData.value.VALUE = null;
      }
    }
    
    if (findData.propertyType == '11') {
      if (!findData.value_acquisition || findData.value_acquisition.VALUE == '0') {
        if (findData.value_acquisition) findData.value_acquisition.VALUE = null;
      }
      if (!findData.value_comparable || findData.value_comparable.VALUE == '0') {
        if (findData.value_comparable) findData.value_comparable.VALUE = null;
      }
      if (!findData.value_income || findData.value_income.VALUE == '0') {
        if (findData.value_income) findData.value_income.VALUE = null;
      }
      if (!findData.value_market || findData.value_market.VALUE == '0') {
        if (findData.value_market) findData.value_market.VALUE = null;
      }
      if (!findData.value_residual || findData.value_residual.VALUE == '0') {
        if (findData.value_residual) findData.value_residual.VALUE = null;
      }
      if (!findData.value_unspecified || findData.value_unspecified.VALUE == '0') {
        if (findData.value_unspecified) findData.value_unspecified.VALUE = null;
      }
      findData.isEditableFlag = value;
    }
    
    return findData;
  }

  checkingForValidSave(scope: ScheduleG1LineItem[]) {
    if (this.saveFlag == false) {
      this.isSaveValidFlag = false;
      this.invalidSaveMessage = "Please complete Schedule G Line 7 before saving Schedule G1. Schedule G Line 7 must be answered with 'Yes' to enable Schedule G1.";
    } else {
      this.isSaveValidFlag = true;
      this.invalidSaveMessage = "Error saving Schedule G data - ";
      
      const line_validation = scope.filter(entry => entry["line_no"] === '5a');
      const line_validation_5c = scope.filter(entry => entry["line_no"] === '5c');
      
      if (line_validation.length > 0 && Array.isArray(line_validation[0].value) && line_validation[0].value[0].VALUE === 'Y') {
        if (line_validation_5c.length > 0) {
          const line5c = line_validation_5c[0];
          
          if ((!line5c.value_comparable || !line5c.value_comparable.VALUE) &&
              (!line5c.value_acquisition || !line5c.value_acquisition.VALUE) &&
              (!line5c.value_income || !line5c.value_income.VALUE) &&
              (!line5c.value_market || !line5c.value_market.VALUE) &&
              (!line5c.value_residual || !line5c.value_residual.VALUE) &&
              (!line5c.value_unspecified || !line5c.value_unspecified.VALUE)) {
            this.isSaveValidFlag = false;
            this.invalidSaveMessage += "Check values for line 5c. ";
          } else if (line5c.value_comparable?.VALUE == '0' && 
                     line5c.value_acquisition?.VALUE == '0' && 
                     line5c.value_income?.VALUE == '0' &&
                     line5c.value_market?.VALUE == '0' && 
                     line5c.value_residual?.VALUE == '0' && 
                     line5c.value_unspecified?.VALUE == '0') {
            this.isSaveValidFlag = false;
            this.invalidSaveMessage += "Check values for line 5c. ";
          } else if (line5c.value_comparable?.VALUE == null && 
                     line5c.value_acquisition?.VALUE == null && 
                     line5c.value_income?.VALUE == null &&
                     line5c.value_market?.VALUE == null && 
                     line5c.value_residual?.VALUE == null && 
                     line5c.value_unspecified?.VALUE == null) {
            this.isSaveValidFlag = false;
            this.invalidSaveMessage += "Check values for line 5c. ";
          }
        }
      }
    }
    
    return this.isSaveValidFlag;
  }

  saveScheduleG1Form() {
    const editRow: any[] = [];
    const scheduleSettings = [{
      "tax_year": this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
      "scenario": this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
      "jcd_key": this.globalService?.globalParams?.jcd_key || 250
    }];

    this.checkingForValidSave(this.irsF5471ScheduleG1Data);

    if (this.irsF5471ScheduleG1Data.length > 0) {
      const temporaryObject = this.irsF5471ScheduleG1Data;

      for (let loopIndex = 0; loopIndex < temporaryObject.length; loopIndex++) {
        if (loopIndex === 6) {
          for (const key in temporaryObject[loopIndex]) {
            if (!temporaryObject[loopIndex].hasOwnProperty(key)) continue;

            const obj = (temporaryObject[loopIndex] as any)[key];
            if (obj && obj.hasOwnProperty && obj.hasOwnProperty('attrib_name')) {
              let value: string | null;
              if (temporaryObject[loopIndex].line_no === '5c') {
                if (obj.VALUE === "0") {
                  value = '';
                } else {
                  // For checkboxes: convert undefined to null (backend requires Attribute_Value)
                  value = obj.VALUE === undefined ? null : obj.VALUE;
                }
              } else {
                value = obj.VALUE === undefined ? null : obj.VALUE;
              }

              const lineAttribKey = obj.line_attrib_key;
              const attribName = obj.attrib_name;

              // Match EXACT AngularJS object structure - Attribute_Value in the middle, before trans_dtls_key
              const saveObject = [{
                "tax_year": this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
                "trans_type_key": temporaryObject[loopIndex].saveParams.trans_type_key,
                "combination_key": this.rowData.HO_COMBINATION_KEY,
                "group_obj_key": this.rowData.GROUP_OBJ_KEY,
                "form_key": temporaryObject[loopIndex].saveParams.form_key,
                "line_no": temporaryObject[loopIndex].line_no,
                "occurence": ((temporaryObject[loopIndex].saveParams.occurrence === "0") ? null : temporaryObject[loopIndex].saveParams.occurrence),
                "line_attrib_key": lineAttribKey,
                "Attribute_Name": attribName,
                "Attribute_Value": value,  // null will be preserved in JSON, not omitted
                "trans_dtls_key": ((temporaryObject[loopIndex].saveParams.trans_Details_key === "0") ? null : temporaryObject[loopIndex].saveParams.trans_Details_key),
                "trans_status": "A"
              }];
              editRow.push(saveObject);
            }
          }
        }

        if (temporaryObject[loopIndex].hasOwnProperty('propertyType') && temporaryObject[loopIndex].propertyType == '13') {
          const valueArray = Array.isArray(temporaryObject[loopIndex].value) ? temporaryObject[loopIndex].value as ScheduleG1LineValue[] : [];
          const attribArray = Array.isArray(temporaryObject[loopIndex].attrib_name) ? temporaryObject[loopIndex].attrib_name as string[] : [];
          
          for (let k = 0; k < valueArray.length; k++) {
            // Match EXACT AngularJS object structure
            const saveObject = [{
              "tax_year": this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
              "trans_type_key": temporaryObject[loopIndex].saveParams.trans_type_key,
              "combination_key": this.rowData.HO_COMBINATION_KEY,
              "group_obj_key": this.rowData.GROUP_OBJ_KEY,
              "form_key": temporaryObject[loopIndex].saveParams.form_key,
              "line_no": temporaryObject[loopIndex].line_no,
              "occurence": ((temporaryObject[loopIndex].saveParams.occurrence === "0") ? null : temporaryObject[loopIndex].saveParams.occurrence),
              "line_attrib_key": valueArray[k].line_attrib_key,
              "Attribute_Name": attribArray[k],
              "Attribute_Value": valueArray[k].VALUE,  // Let JSON.stringify handle undefined vs null
              "trans_dtls_key": ((temporaryObject[loopIndex].saveParams.trans_Details_key === "0") ? null : temporaryObject[loopIndex].saveParams.trans_Details_key),
              "trans_status": "A"
            }];
            editRow.push(saveObject);
          }
        } else {
          const value = !Array.isArray(temporaryObject[loopIndex].value) ? temporaryObject[loopIndex].value as ScheduleG1LineValue : null;
          
          if (value) {
            // For LINE_5B specifically: convert undefined to null (backend requires Attribute_Value)
            // For other lines: keep undefined so it gets omitted from JSON
            const lineNo = temporaryObject[loopIndex].line_no;
            const attrValue = (lineNo === '5b' && value.VALUE === undefined) ? null : value.VALUE;
            
            // Match EXACT AngularJS object structure
            const saveObject = [{
              "tax_year": this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
              "trans_type_key": temporaryObject[loopIndex].saveParams.trans_type_key,
              "combination_key": this.rowData.HO_COMBINATION_KEY,
              "group_obj_key": this.rowData.GROUP_OBJ_KEY,
              "form_key": temporaryObject[loopIndex].saveParams.form_key,
              "line_no": lineNo,
              "occurence": ((temporaryObject[loopIndex].saveParams.occurrence === "0") ? null : temporaryObject[loopIndex].saveParams.occurrence),
              "line_attrib_key": value.line_attrib_key,
              "Attribute_Name": temporaryObject[loopIndex].attrib_name,
              "Attribute_Value": attrValue,
              "trans_dtls_key": ((temporaryObject[loopIndex].saveParams.trans_Details_key === "0") ? null : temporaryObject[loopIndex].saveParams.trans_Details_key),
              "trans_status": "A"
            }];
            editRow.push(saveObject);
          }
        }
      }
    }

    if (this.isSaveValidFlag) {
      const scheduleDetails = { "addrows": [], "editrows": editRow, "removerows": [] };
      const message = "Schedule G1 details successfully saved";

      this.crudLoading = true;

      this.fir5471ScheduleG1Service.saveScheduleG1(
        this.baseURLs.api,
        scheduleSettings,
        scheduleDetails,
        this.globalService?.globalParams || {},
        this.filterParams,
        this.rowData
      ).pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: any) => {
        this.crudLoading = false;
        
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          const updateEvent = new CustomEvent('request-grid-update', {
            detail: 'refresh',
            bubbles: true
          });

          const element = document.querySelector('gtw-fir-5471-schedule-g1-ce');
          if (element) {
            element.dispatchEvent(updateEvent);
          }
        }
      }, (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('An error occurred while saving. Please try again.', 'gtw-snackbar--red');
      });
    } else {
      this.gtwSnackbarService.openSnackBar(this.invalidSaveMessage, 'gtw-snackbar--red');
    }
  }

  cancel() {
    const customEvent = new CustomEvent('request-grid-update', { 
      detail: 'cancel',
      bubbles: true 
    });
    
    const element = document.querySelector('gtw-fir-5471-schedule-g1-ce');
    if (element) {
      element.dispatchEvent(customEvent);
    }
  }

  // Helper methods for template
  isArray(value: any): value is any[] {
    return Array.isArray(value);
  }

  getCheckboxValue(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  onCheckboxChange(row: ScheduleG1LineItem, field: 'comparable' | 'income' | 'acquisition' | 'market' | 'residual' | 'unspecified', event: Event, index: number) {
    const checked = this.getCheckboxValue(event);
    const value = checked ? '1' : '0';
    
    switch(field) {
      case 'comparable':
        if (row.value_comparable) row.value_comparable.VALUE = value;
        break;
      case 'income':
        if (row.value_income) row.value_income.VALUE = value;
        break;
      case 'acquisition':
        if (row.value_acquisition) row.value_acquisition.VALUE = value;
        break;
      case 'market':
        if (row.value_market) row.value_market.VALUE = value;
        break;
      case 'residual':
        if (row.value_residual) row.value_residual.VALUE = value;
        break;
      case 'unspecified':
        if (row.value_unspecified) row.value_unspecified.VALUE = value;
        break;
    }
    
    this.checkingForValidation(row, index);
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}

